/**
 * Ranking Engine — the single most important card on the dashboard.
 * One focused game interface. Premium dark UI, green + purple neon accents.
 *
 * Data sources (all real, scoped to the logged-in user's client_id):
 *   - keyword_groups → top 3 target/opportunity groups (Option A, B, Next)
 *   - daily_moves    → has today's move been executed already?
 *   - daily_moves    → last move timestamp for the "Last move: …" subtext
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOwnClientId } from "@/hooks/useOwnClientId";
import { isDemoMode } from "@/lib/demoMode";
import { normalizeKeyword } from "@/lib/keywordGroups";
import {
  Activity,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  Clock,
  Crosshair,
  Eye,
  FileText,
  Link2,
  Loader2,
  Minus,
  Network,
  Rocket,
  Sparkles,
  Star,
  Target,
  Trophy,
  Users,
  Zap,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────
// Demo Ranking Panel — seeded, animated mock for the cinematic landing demo.
// Renders only when demo mode is active so the Ranking Engine never sits on
// "Scanning your keywords…". Simulates live ranking movement.
// ──────────────────────────────────────────────────────────────────────────

interface DemoRow {
  keyword: string;
  position: number;
  prev: number;
  impressions: number;
  trend: number[]; // last 8 positions, newest last
}

const DEMO_SEED: DemoRow[] = [
  { keyword: "locksmith sandton", position: 3, prev: 5, impressions: 1240, trend: [9, 8, 7, 7, 6, 5, 4, 3] },
  { keyword: "emergency locksmith johannesburg", position: 5, prev: 7, impressions: 980, trend: [12, 11, 9, 9, 8, 7, 6, 5] },
  { keyword: "car key replacement sandton", position: 2, prev: 4, impressions: 760, trend: [8, 7, 6, 5, 5, 4, 3, 2] },
  { keyword: "24 hour locksmith near me", position: 6, prev: 6, impressions: 1520, trend: [7, 6, 6, 7, 6, 6, 6, 6] },
  { keyword: "lock repair rivonia", position: 4, prev: 8, impressions: 540, trend: [11, 10, 9, 8, 7, 6, 5, 4] },
  { keyword: "safe opening sandton", position: 9, prev: 12, impressions: 320, trend: [16, 15, 14, 13, 12, 11, 10, 9] },
];

function Sparkline({ points, color = "hsl(150 95% 55%)" }: { points: number[]; color?: string }) {
  // Lower position = better, so invert Y. Range 1..20.
  const w = 64;
  const h = 18;
  const path = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = ((Math.min(20, Math.max(1, p)) - 1) / 19) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${(h - y).toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-90">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ filter: `drop-shadow(0 0 3px ${color})` }} />
    </svg>
  );
}

function DemoRankingPanel() {
  const [rows, setRows] = useState<DemoRow[]>(DEMO_SEED);
  const [tick, setTick] = useState(0);
  const [visibility, setVisibility] = useState(72);
  const [flashIdx, setFlashIdx] = useState<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => {
      setTick((t) => t + 1);
      setRows((prev) => {
        const next = prev.map((r) => ({ ...r, prev: r.position }));
        // Pick a random row to nudge
        const i = Math.floor(Math.random() * next.length);
        const delta = Math.random() < 0.6 ? -1 : Math.random() < 0.5 ? 1 : 0;
        const newPos = Math.max(1, Math.min(18, next[i].position + delta));
        next[i] = {
          ...next[i],
          position: newPos,
          impressions: next[i].impressions + Math.floor(Math.random() * 25),
          trend: [...next[i].trend.slice(1), newPos],
        };
        setFlashIdx(i);
        window.setTimeout(() => setFlashIdx(null), 900);
        return next;
      });
      setVisibility((v) => Math.min(98, Math.max(60, v + (Math.random() < 0.7 ? 1 : -1) * (Math.random() < 0.5 ? 0.3 : 0.6))));
    }, 2200);
    return () => window.clearInterval(id);
  }, []);

  const avgPos = useMemo(() => (rows.reduce((s, r) => s + r.position, 0) / rows.length).toFixed(1), [rows]);
  const totalImp = useMemo(() => rows.reduce((s, r) => s + r.impressions, 0), [rows]);

  return (
    <section
      data-tour-id="ranking-engine"
      className="relative overflow-hidden rounded-2xl border border-border/60 p-3 sm:p-5 backdrop-blur-md"
      style={{
        background:
          "radial-gradient(120% 80% at 0% 0%, hsl(150 70% 12% / 0.35), transparent 55%)," +
          "radial-gradient(120% 80% at 100% 0%, hsl(270 70% 14% / 0.35), transparent 55%)," +
          "linear-gradient(180deg, hsl(220 25% 7% / 0.8), hsl(220 25% 5% / 0.85))",
      }}
    >
      {/* Header */}
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/40 bg-primary/10"
            style={{ boxShadow: "0 0 18px hsl(150 95% 55% / 0.25)" }}
          >
            <Target className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              Ranking Engine
              <span className="flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-bold tracking-wider text-primary">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                </span>
                LIVE
              </span>
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              Tracking {rows.length} keywords · Updated just now
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <Sparkles className="h-3 w-3 text-primary" />
          AI continuously optimizing
        </div>
      </header>

      {/* Stat strip */}
      <div data-tour-id="re-stats" className="mb-4 grid grid-cols-3 gap-2 sm:gap-3">
        {[
          { label: "Visibility", value: `${visibility.toFixed(0)}%`, icon: Eye, accent: "150 95% 55%", tour: "re-visibility" },
          { label: "Avg. Position", value: avgPos, icon: Trophy, accent: "270 95% 65%", tour: "re-avgpos" },
          { label: "Impressions", value: totalImp.toLocaleString(), icon: Activity, accent: "150 95% 55%", tour: "re-impressions" },
        ].map((s) => (
          <div
            key={s.label}
            data-tour-id={s.tour}
            className="rounded-xl border border-border/50 p-2.5 sm:p-3"
            style={{
              background: `linear-gradient(135deg, hsl(${s.accent} / 0.10), hsl(220 25% 6% / 0.6))`,
              boxShadow: `inset 0 0 30px hsl(${s.accent} / 0.06)`,
            }}
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              <s.icon className="h-3 w-3" style={{ color: `hsl(${s.accent})` }} />
              {s.label}
            </div>
            <div
              className="mt-1 text-lg sm:text-xl font-bold text-foreground tabular-nums transition-all"
              style={{ textShadow: `0 0 12px hsl(${s.accent} / 0.35)` }}
            >
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Keyword rows */}
      <div data-tour-id="re-rows" className="space-y-1.5">
        {rows.map((r, i) => {
          const change = r.prev - r.position; // +ve = improved
          const isFlash = flashIdx === i;
          return (
            <div
              key={r.keyword}
              className="relative grid grid-cols-[auto,1fr,auto,auto] items-center gap-2 sm:gap-3 rounded-lg border border-border/40 px-2.5 sm:px-3 py-2 transition-all duration-500"
              style={{
                background: isFlash
                  ? "linear-gradient(90deg, hsl(150 95% 55% / 0.14), hsl(220 25% 6% / 0.6))"
                  : "linear-gradient(90deg, hsl(220 25% 8% / 0.5), hsl(220 25% 6% / 0.5))",
                boxShadow: isFlash ? "0 0 20px hsl(150 95% 55% / 0.25)" : "none",
              }}
            >
              {/* Rank pill */}
              <div
                className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-md text-sm font-bold tabular-nums transition-all"
                style={{
                  background:
                    r.position <= 3
                      ? "linear-gradient(180deg, hsl(150 95% 55%), hsl(150 90% 40%))"
                      : r.position <= 10
                      ? "linear-gradient(180deg, hsl(270 90% 65%), hsl(270 85% 50%))"
                      : "hsl(220 15% 18%)",
                  color: r.position <= 10 ? "hsl(220 25% 8%)" : "hsl(220 10% 70%)",
                  boxShadow:
                    r.position <= 3
                      ? "0 0 16px hsl(150 95% 55% / 0.55)"
                      : r.position <= 10
                      ? "0 0 14px hsl(270 90% 65% / 0.45)"
                      : "none",
                }}
              >
                #{r.position}
              </div>

              {/* Keyword + meta */}
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-foreground">{r.keyword}</div>
                <div className="text-[10.5px] text-muted-foreground tabular-nums">
                  {r.impressions.toLocaleString()} impressions
                </div>
              </div>

              {/* Sparkline */}
              <div className="hidden sm:block">
                <Sparkline
                  points={r.trend}
                  color={r.position <= 3 ? "hsl(150 95% 55%)" : "hsl(270 90% 70%)"}
                />
              </div>

              {/* Movement */}
              <div
                className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums"
                style={{
                  background:
                    change > 0
                      ? "hsl(150 95% 55% / 0.14)"
                      : change < 0
                      ? "hsl(0 80% 60% / 0.14)"
                      : "hsl(220 15% 18%)",
                  color:
                    change > 0
                      ? "hsl(150 95% 60%)"
                      : change < 0
                      ? "hsl(0 85% 70%)"
                      : "hsl(220 10% 65%)",
                }}
              >
                {change > 0 ? <ArrowUp className="h-3 w-3" /> : change < 0 ? <ArrowDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                {change === 0 ? "—" : Math.abs(change)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between text-[10.5px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
          </span>
          Live ranking feed · tick #{tick + 1}
        </span>
        <span>Local SEO · Sandton, ZA</span>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────

interface KeywordGroupRow {
  id: string;
  canonical_keyword: string;
  normalized_key: string;
  status: string;
  best_position: number | null;
  total_impressions: number;
  total_clicks: number;
  variants: unknown;
}

interface DailyMoveRow {
  id: string;
  selected_keyword: string;
  normalized_key: string;
  executed_by: string;
  executed_at: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function variantsCount(v: unknown): number {
  if (Array.isArray(v)) return v.length;
  return 0;
}

function demandLabel(impressions: number): { text: string; level: number } {
  // level: 0 building → 4 very high (used for bar fill)
  if (impressions >= 50) return { text: "Very High", level: 4 };
  if (impressions >= 20) return { text: "High", level: 3 };
  if (impressions >= 5) return { text: "Medium", level: 2 };
  if (impressions > 0) return { text: "Low", level: 1 };
  return { text: "Building", level: 0 };
}

function formatRelative(ts: string | null): string {
  if (!ts) return "—";
  const diffMs = Date.now() - new Date(ts).getTime();
  const m = Math.max(0, Math.round(diffMs / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function nextSixUtcMs(): number {
  const now = new Date();
  const next = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 6, 0, 0, 0),
  );
  if (next.getTime() <= now.getTime()) {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  return next.getTime();
}

function fmtCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────

const POSITION_BLOCKS = 8; // segments in the position progress bar
const DEMAND_BLOCKS = 12; // segments in the demand bar

function PositionBar({ position }: { position: number | null }) {
  // Map: pos 20 → 1 block, pos 1 → 8 blocks
  const filled = useMemo(() => {
    if (position == null) return 0;
    const p = Math.max(1, Math.min(20, position));
    // Linear: 20 → 1 block, 1 → POSITION_BLOCKS
    const ratio = (20 - p) / 19;
    return Math.max(1, Math.round(ratio * POSITION_BLOCKS));
  }, [position]);

  const [animFilled, setAnimFilled] = useState(0);
  useEffect(() => {
    setAnimFilled(0);
    const id = window.setTimeout(() => setAnimFilled(filled), 60);
    return () => window.clearTimeout(id);
  }, [filled]);

  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: POSITION_BLOCKS }).map((_, i) => {
        const on = i < animFilled;
        return (
          <div
            key={i}
            className="h-3 flex-1 rounded-sm transition-all duration-500"
            style={{
              background: on
                ? "linear-gradient(180deg, hsl(150 95% 60%), hsl(150 90% 45%))"
                : "hsl(220 15% 16%)",
              boxShadow: on ? "0 0 10px hsl(150 95% 55% / 0.55)" : "none",
              transitionDelay: `${i * 40}ms`,
            }}
          />
        );
      })}
    </div>
  );
}

function DemandBar({ level, color = "purple" }: { level: number; color?: "purple" | "green" }) {
  // level 0..4 → fill ratio
  const filled = useMemo(() => {
    if (level <= 0) return 0;
    return Math.max(1, Math.round((level / 4) * DEMAND_BLOCKS));
  }, [level]);

  const [animFilled, setAnimFilled] = useState(0);
  useEffect(() => {
    setAnimFilled(0);
    const id = window.setTimeout(() => setAnimFilled(filled), 60);
    return () => window.clearTimeout(id);
  }, [filled]);

  const onGrad =
    color === "purple"
      ? "linear-gradient(180deg, hsl(270 95% 70%), hsl(270 90% 55%))"
      : "linear-gradient(180deg, hsl(150 95% 60%), hsl(150 90% 45%))";
  const onGlow =
    color === "purple" ? "0 0 10px hsl(270 95% 65% / 0.55)" : "0 0 10px hsl(150 95% 55% / 0.55)";

  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: DEMAND_BLOCKS }).map((_, i) => {
        const on = i < animFilled;
        return (
          <div
            key={i}
            className="h-3 flex-1 rounded-sm transition-all duration-500"
            style={{
              background: on ? onGrad : "hsl(220 15% 16%)",
              boxShadow: on ? onGlow : "none",
              transitionDelay: `${i * 30}ms`,
            }}
          />
        );
      })}
    </div>
  );
}

function PulseLine() {
  return (
    <svg width="60" height="16" viewBox="0 0 60 16" className="opacity-80">
      <polyline
        points="0,8 8,8 12,4 16,12 22,6 28,9 34,3 40,11 46,7 52,8 60,8"
        fill="none"
        stroke="hsl(150 95% 55%)"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        style={{ filter: "drop-shadow(0 0 4px hsl(150 95% 55% / 0.7))" }}
      />
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────

const RankingEngine = () => {
  if (isDemoMode()) {
    return <DemoRankingPanel />;
  }
  return <RankingEngineInner />;
};

const RankingEngineInner = () => {
  // Demo mode: always render the seeded live demo panel so the cinematic tour
  // never lands on a stuck "Scanning your keywords…" state.
  const { ownClientId, resolved } = useOwnClientId("RankingEngine");

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<KeywordGroupRow[]>([]);
  const [allGroups, setAllGroups] = useState<KeywordGroupRow[]>([]);
  const [todayMove, setTodayMove] = useState<DailyMoveRow | null>(null);
  const [lastMoveAt, setLastMoveAt] = useState<string | null>(null);
  const [selected, setSelected] = useState<"A" | "B">("A");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justExecuted, setJustExecuted] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());

  const targetMs = useRef<number>(nextSixUtcMs());

  // Tick the countdown every second
  useEffect(() => {
    const id = window.setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= targetMs.current) {
        targetMs.current = nextSixUtcMs();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  // Load real data
  useEffect(() => {
    if (!resolved) return;
    if (!ownClientId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const today = new Date().toLocaleDateString('en-CA');
        // Align exclusion window with backend (generate-daily-article uses
        // a 60-day blog_posts lookback to avoid re-targeting the same keyword).
        const sixtyDaysAgo = new Date(
          Date.now() - 60 * 24 * 60 * 60 * 1000,
        ).toISOString();

        const [groupsRes, todayRes, lastRes, recentPostsRes] = await Promise.all([
          supabase
            .from("keyword_groups")
            .select(
              "id, canonical_keyword, normalized_key, status, best_position, total_impressions, total_clicks, variants",
            )
            .eq("client_id", ownClientId)
            .in("status", ["target", "opportunity"]),
          supabase
            .from("daily_moves")
            .select("id, selected_keyword, normalized_key, executed_by, executed_at")
            .eq("client_id", ownClientId)
            .eq("move_date", today)
            .maybeSingle(),
          supabase
            .from("daily_moves")
            .select("executed_at")
            .eq("client_id", ownClientId)
            .order("executed_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from("blog_posts")
            .select("keyword")
            .eq("client_id", ownClientId)
            .gte("created_at", sixtyDaysAgo),
        ]);

        if (cancelled) return;

        // Mirror backend filter: exclude any keyword group whose normalized
        // form matches a blog_posts.keyword written in the last 60 days.
        const recentKeys = new Set<string>();
        for (const p of (recentPostsRes.data || []) as { keyword: string | null }[]) {
          const k = normalizeKeyword(String(p.keyword || ""));
          if (k) recentKeys.add(k);
        }
        const allRows = (groupsRes.data || []) as KeywordGroupRow[];
        let rows = allRows.filter((r) => !recentKeys.has(r.normalized_key));
        // Safety: if the filter wipes everything (small accounts), fall back
        // to the full list so the card still renders something useful.
        if (rows.length === 0) rows = allRows;
        // Sort: surgical zone first (pos 4..15 with imps), then by impressions desc, then by best_position asc
        rows.sort((a, b) => {
          const aSurg =
            a.best_position != null &&
            a.best_position >= 4 &&
            a.best_position <= 15 &&
            (a.total_impressions ?? 0) > 0
              ? 0
              : 1;
          const bSurg =
            b.best_position != null &&
            b.best_position >= 4 &&
            b.best_position <= 15 &&
            (b.total_impressions ?? 0) > 0
              ? 0
              : 1;
          if (aSurg !== bSurg) return aSurg - bSurg;
          const ia = a.total_impressions ?? 0;
          const ib = b.total_impressions ?? 0;
          if (ia !== ib) return ib - ia;
          const pa = a.best_position ?? Number.POSITIVE_INFINITY;
          const pb = b.best_position ?? Number.POSITIVE_INFINITY;
          return pa - pb;
        });

        setGroups(rows.slice(0, 3));
        setAllGroups(allRows);
        setTodayMove((todayRes.data as DailyMoveRow | null) ?? null);
        setLastMoveAt((lastRes.data as { executed_at: string } | null)?.executed_at ?? null);
      } catch (e) {
        console.error("[RankingEngine] load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownClientId, resolved, justExecuted]);

  const optionA = groups[0] ?? null;
  const optionB = groups[1] ?? null;
  const nextTarget = groups[2] ?? groups[1] ?? null;

  const alreadyExecuted = todayMove != null || justExecuted;

  // Hero must reflect the ACTUALLY EXECUTED move when one exists for today.
  // Look up the matching group (by normalized_key) from the full unfiltered
  // list so we still get accurate position/impressions/variants, even if the
  // 60-day exclusion removed it from the candidate set. Fall back to a
  // synthetic minimal group if no metadata is available.
  const heroGroup: KeywordGroupRow | null = useMemo(() => {
    if (todayMove) {
      const match = allGroups.find(
        (g) => g.normalized_key === todayMove.normalized_key,
      );
      if (match) return match;
      return {
        id: `today-${todayMove.id}`,
        canonical_keyword: todayMove.selected_keyword,
        normalized_key: todayMove.normalized_key,
        status: "target",
        best_position: null,
        total_impressions: 0,
        total_clicks: 0,
        variants: [],
      };
    }
    return optionA;
  }, [todayMove, allGroups, optionA]);
  const heroPos = heroGroup?.best_position != null ? Math.round(heroGroup.best_position) : null;
  const heroDemand = demandLabel(heroGroup?.total_impressions ?? 0);

  const handleAttack = async () => {
    if (!ownClientId) return;
    const { blockIfDemo } = await import("@/lib/demoMode");
    if (blockIfDemo("Launching an attack")) return;
    const choice = selected === "A" ? optionA : optionB;
    if (!choice) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data, error } = await supabase.functions.invoke("execute-move", {
        body: {
          client_id: ownClientId,
          keyword_override: choice.canonical_keyword,
          normalized_key: choice.normalized_key,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      setJustExecuted(true);
    } catch (e) {
      setSubmitError((e as Error).message || "Failed to launch attack");
    } finally {
      setSubmitting(false);
    }
  };

  // ────────── Empty / loading states ──────────

  if (!resolved || loading) {
    return (
      <section className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur-md">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Scanning your keywords…
        </div>
      </section>
    );
  }

  if (!ownClientId || !heroGroup) {
    return (
      <section className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur-md">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Activity className="h-4 w-4 text-primary" />
          {groups.length === 0
            ? "Scanning your keywords…"
            : "All positions secured — monitoring."}
        </div>
      </section>
    );
  }

  const variants = variantsCount(heroGroup.variants);
  const countdown = fmtCountdown(targetMs.current - now);

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-border/60 p-3 sm:p-5 backdrop-blur-md"
      style={{
        background:
          "radial-gradient(120% 80% at 0% 0%, hsl(150 70% 12% / 0.35), transparent 55%)," +
          "radial-gradient(120% 80% at 100% 0%, hsl(270 70% 14% / 0.35), transparent 55%)," +
          "linear-gradient(180deg, hsl(220 25% 7% / 0.8), hsl(220 25% 5% / 0.85))",
      }}
    >
      {/* ────────── Top bar ────────── */}
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/40 bg-primary/10"
            style={{ boxShadow: "0 0 18px hsl(150 95% 55% / 0.25)" }}
          >
            <Target className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              Ranking Engine
              <span className="flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-bold tracking-wider text-primary">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                </span>
                ACTIVE
              </span>
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              Last move: {formatRelative(lastMoveAt)} · Next auto-run: 06:00 UTC
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          Engine running smoothly
          <PulseLine />
        </div>
      </header>

      {/* ────────── Section 1 — Today's move ────────── */}
      <div
        className="relative overflow-hidden rounded-xl border border-primary/25 p-4 sm:p-5"
        style={{
          background:
            "linear-gradient(135deg, hsl(150 40% 6% / 0.7), hsl(220 25% 4% / 0.95))",
        }}
      >
        <div className="grid gap-5 md:grid-cols-[1.4fr,1fr]">
          {/* Left */}
          <div className="flex gap-3 sm:gap-4">
            <div
              className="relative flex h-12 w-12 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-xl border border-primary/40 bg-primary/10"
            >
              <Crosshair
                className="h-6 w-6 sm:h-8 sm:w-8 text-primary"
                style={{ animation: "spin 60s linear infinite" }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] sm:text-[11px] font-bold tracking-wider text-primary">
                <span className="inline-flex items-center gap-1.5">
                  <Target className="h-3 w-3" />
                  TODAY&apos;S MOVE
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider ${
                    alreadyExecuted
                      ? "border-primary/50 bg-primary/15 text-primary"
                      : "border-primary/40 bg-primary/10 text-primary"
                  }`}
                >
                  <Zap className="h-3 w-3" />
                  {alreadyExecuted ? "ATTACKING" : "READY"}
                </span>
              </div>
              <h2
                className="mt-1.5 break-words text-lg sm:text-3xl font-bold leading-tight text-foreground sm:truncate"
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", overflowWrap: "anywhere", wordBreak: "break-word" }}
              >
                {heroGroup.canonical_keyword}
              </h2>
              <div
                className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-muted-foreground"
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
              >
                <span>
                  Pos <span className="font-bold text-primary">#{heroPos ?? "—"}</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <ArrowRight className="h-3 w-3" /> Top 3
                </span>
                <span>
                  Impr.{" "}
                  <span
                    className={
                      heroDemand.level >= 3
                        ? "font-semibold text-primary"
                        : "font-semibold text-foreground"
                    }
                  >
                    {heroDemand.text}
                  </span>
                </span>
              </div>
            </div>
          </div>


          {/* Right — two columns */}
          <div className="grid grid-cols-2 gap-4 md:border-l md:border-border/40 md:pl-5 border-t border-border/40 pt-4 md:border-t-0 md:pt-0">
            <div>
              <div className="text-[11px] font-semibold text-primary">Why this:</div>
              <ul className="mt-2 space-y-1.5 text-xs text-foreground/90">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> High demand
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Close to breakthrough
                </li>
                <li className="flex items-center gap-2">
                  {alreadyExecuted ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 text-primary" /> Article published
                    </>
                  ) : (
                    <>
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" /> No article yet
                    </>
                  )}
                </li>
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-primary">Action:</div>
              <ul className="mt-2 space-y-1.5 text-xs text-foreground/90">
                <li className="flex items-center gap-2">
                  {alreadyExecuted ? (
                    <CheckCircle2 className="h-3 w-3 text-primary" />
                  ) : (
                    <FileText className="h-3 w-3 text-primary" />
                  )}
                  {alreadyExecuted ? "Article written" : "Writing focused article"}
                </li>
                <li className="flex items-center gap-2">
                  {alreadyExecuted ? (
                    <CheckCircle2 className="h-3 w-3 text-primary" />
                  ) : (
                    <Link2 className="h-3 w-3 text-primary" />
                  )}
                  {alreadyExecuted ? "Group strengthened" : "Strengthening this group"}
                </li>
                <li className="flex items-center gap-2">
                  {alreadyExecuted ? (
                    <CheckCircle2 className="h-3 w-3 text-primary" />
                  ) : (
                    <Network className="h-3 w-3 text-primary" />
                  )}
                  {alreadyExecuted ? "Links added" : "Adding internal links"}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-4 flex items-center justify-between border-t border-border/40 pt-3 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-3.5 w-3.5 text-primary/80" />
            {variants > 1
              ? `Grouping: ${variants} keyword variants merged`
              : "Grouping: single keyword"}
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background/40 px-2.5 py-1 text-[11px] text-foreground/80 hover:bg-background/70"
          >
            <Eye className="h-3 w-3" />
            View details
          </button>
        </div>
      </div>

      {/* ────────── Section 2 — Progress bars ────────── */}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {/* Position progress */}
        <div className="rounded-xl border border-border/60 bg-background/40 p-4 backdrop-blur-sm">
          <div className="mb-3 text-[11px] font-bold tracking-wider text-primary">
            POSITION PROGRESS
          </div>
          <div className="flex items-center gap-3">
            <div
              className="text-3xl font-bold text-primary"
              style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
            >
              {heroPos ?? "—"}
            </div>
            <PositionBar position={heroGroup.best_position} />
            <div className="whitespace-nowrap text-xs text-muted-foreground">
              <ArrowRight className="inline h-3 w-3" /> Top 3
            </div>
          </div>
          <div
            className="mt-2 flex justify-between text-[10px] text-muted-foreground"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            <span>20</span>
            <span>10</span>
            <span className="font-bold text-primary">{heroPos ?? "—"}</span>
            <span>3</span>
            <span>1</span>
          </div>
        </div>

        {/* Demand strength */}
        <div className="rounded-xl border border-border/60 bg-background/40 p-4 backdrop-blur-sm">
          <div className="mb-3 text-[11px] font-bold tracking-wider" style={{ color: "hsl(270 95% 70%)" }}>
            DEMAND STRENGTH (IMPRESSIONS)
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <DemandBar level={heroDemand.level} color="purple" />
            </div>
            <div
              className="whitespace-nowrap text-sm font-semibold"
              style={{ color: "hsl(270 95% 70%)" }}
            >
              {heroDemand.text}
            </div>
          </div>
          <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
            <span>Low</span>
            <span>Medium</span>
            <span style={{ color: "hsl(270 95% 70%)" }} className="font-bold">
              High
            </span>
            <span>Very High</span>
          </div>
        </div>
      </div>

      {/* ────────── Section 3 — Choose today's move ────────── */}
      {alreadyExecuted ? (
        <div
          className="mt-4 flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm font-semibold text-primary"
          style={{ boxShadow: "0 0 24px hsl(150 95% 55% / 0.18)" }}
        >
          <CheckCircle2 className="h-4 w-4" />
          Today&apos;s move complete
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            {todayMove?.executed_by === "user" ? "Triggered by you" : "Auto-executed"} ·{" "}
            {formatRelative(todayMove?.executed_at ?? new Date().toISOString())}
          </span>
        </div>
      ) : (
        <div
          className="mt-4 rounded-xl border border-border/60 p-4"
          style={{
            background:
              "linear-gradient(135deg, hsl(45 70% 8% / 0.45), hsl(220 25% 6% / 0.7))",
          }}
        >
          <div className="mb-1 flex items-center gap-2 text-[11px] font-bold tracking-wider" style={{ color: "hsl(45 95% 60%)" }}>
            <Zap className="h-3 w-3" /> CHOOSE TODAY&apos;S MOVE
          </div>
          <div className="mb-3 text-xs text-muted-foreground">
            You choose the direction. We execute the plan.
          </div>

          <div className={`grid gap-3 ${optionB ? "md:grid-cols-2" : ""}`}>
            {/* Option A */}
            <button
              type="button"
              onClick={() => setSelected("A")}
              className={`relative rounded-lg border p-3 text-left transition-all ${
                selected === "A"
                  ? "border-primary/70"
                  : "border-border/60 hover:border-border"
              }`}
              style={{
                background:
                  selected === "A"
                    ? "linear-gradient(135deg, hsl(150 70% 10% / 0.55), hsl(220 25% 6% / 0.7))"
                    : "hsl(220 25% 7% / 0.6)",
                boxShadow:
                  selected === "A" ? "0 0 24px hsl(150 95% 55% / 0.25)" : "none",
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border ${
                      selected === "A" ? "border-primary" : "border-border"
                    }`}
                  >
                    {selected === "A" && (
                      <span
                        className="h-2 w-2 rounded-full bg-primary"
                        style={{ boxShadow: "0 0 8px hsl(150 95% 55%)" }}
                      />
                    )}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      <span className="text-primary">A.</span> {optionA.canonical_keyword}
                    </div>
                  </div>
                </div>
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[9px] font-bold tracking-wider text-primary"
                >
                  <Star className="h-2.5 w-2.5" /> RECOMMENDED
                </span>
              </div>
              <div
                className="mt-2 text-xs text-muted-foreground"
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
              >
                Position:{" "}
                <span className="text-foreground">
                  #{optionA.best_position != null ? Math.round(optionA.best_position) : "—"}
                </span>{" "}
                → Top 3
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">Demand:</span>
                <div className="flex-1">
                  <DemandBar
                    level={demandLabel(optionA.total_impressions ?? 0).level}
                    color="purple"
                  />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-primary">
                <Zap className="h-3 w-3" /> Faster ranking win
              </div>
            </button>

            {/* Option B */}
            {optionB && (
              <button
                type="button"
                onClick={() => setSelected("B")}
                className={`relative rounded-lg border p-3 text-left transition-all ${
                  selected === "B"
                    ? "border-[hsl(270_95%_65%)]"
                    : "border-border/60 hover:border-border"
                }`}
                style={{
                  background:
                    selected === "B"
                      ? "linear-gradient(135deg, hsl(270 70% 12% / 0.5), hsl(220 25% 6% / 0.7))"
                      : "hsl(220 25% 7% / 0.6)",
                  boxShadow:
                    selected === "B" ? "0 0 24px hsl(270 95% 65% / 0.25)" : "none",
                }}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border ${
                      selected === "B" ? "border-[hsl(270_95%_70%)]" : "border-border"
                    }`}
                  >
                    {selected === "B" && (
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{
                          background: "hsl(270 95% 70%)",
                          boxShadow: "0 0 8px hsl(270 95% 65%)",
                        }}
                      />
                    )}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      <span style={{ color: "hsl(270 95% 70%)" }}>B.</span>{" "}
                      {optionB.canonical_keyword}
                    </div>
                  </div>
                </div>
                <div
                  className="mt-2 text-xs text-muted-foreground"
                  style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                >
                  Position:{" "}
                  <span className="text-foreground">
                    #{optionB.best_position != null ? Math.round(optionB.best_position) : "—"}
                  </span>{" "}
                  → Top 5
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">Demand:</span>
                  <div className="flex-1">
                    <DemandBar
                      level={demandLabel(optionB.total_impressions ?? 0).level}
                      color="purple"
                    />
                  </div>
                </div>
                <div
                  className="mt-2 flex items-center gap-1.5 text-[11px]"
                  style={{ color: "hsl(270 95% 70%)" }}
                >
                  <Trophy className="h-3 w-3" /> Bigger traffic potential
                </div>
              </button>
            )}
          </div>

          {/* Action row */}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={handleAttack}
              disabled={submitting}
              className="group relative inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold text-primary-foreground transition-all disabled:cursor-not-allowed disabled:opacity-60"
              style={{
                background:
                  "linear-gradient(180deg, hsl(150 95% 50%), hsl(150 90% 38%))",
                boxShadow:
                  "0 0 28px hsl(150 95% 55% / 0.5), inset 0 1px 0 hsl(150 95% 70% / 0.6)",
              }}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4" />
              )}
              {submitting ? "LAUNCHING…" : "ATTACK SELECTED TARGET"}
            </button>
            <div
              className="inline-flex items-center gap-2 rounded-lg border border-border/60 bg-background/40 px-3 py-2.5 text-xs text-muted-foreground"
            >
              <Clock className="h-3.5 w-3.5" />
              Auto-executes in{" "}
              <span
                className="font-bold"
                style={{
                  color: "hsl(45 95% 60%)",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {countdown}
              </span>
            </div>
          </div>
          {submitError && (
            <div className="mt-2 text-xs text-destructive">{submitError}</div>
          )}
        </div>
      )}

      {/* ────────── Section 4 — Next target footer ────────── */}
      {nextTarget && nextTarget.id !== heroGroup.id && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/40 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary"
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
            <span className="text-[11px] font-bold tracking-wider text-primary">
              NEXT TARGET
            </span>
            <span className="text-foreground">{nextTarget.canonical_keyword}</span>
            <span className="rounded-md border border-border/60 bg-background/60 px-2 py-0.5 text-[10px] text-muted-foreground">
              Position: #
              {nextTarget.best_position != null
                ? Math.round(nextTarget.best_position)
                : "—"}
            </span>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-foreground/80 hover:text-foreground"
          >
            View queue <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </section>
  );
};

export default RankingEngine;
